import { useState } from "react";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Checkbox } from "@volca/ui/components/ui/checkbox";
import { Input } from "@volca/ui/components/ui/input";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { StepLayout } from "./step-layout";
import { StepNavigation } from "./step-navigation";
import {
  INVOICE_SOURCE,
  type InvoiceSource,
  type OnboardingAnswers,
} from "./types";

// Matches LONG_TEXT_ANSWER_LIMIT on BusinessOnboarding, which refuses anything longer.
const INVOICE_SOURCES_SUMMARY_LIMIT = 2000;

const SOURCE_GROUPS: {
  heading: string;
  options: { value: InvoiceSource; label: string; detail: string }[];
}[] = [
  {
    heading: "By email",
    options: [
      {
        value: INVOICE_SOURCE.masterInbox,
        label: "One shared inbox",
        detail: "Vendors send invoices to a single address, such as ap@.",
      },
      {
        value: INVOICE_SOURCE.individualMailboxes,
        label: "People's own mailboxes",
        detail: "Invoices arrive in the mailboxes of the people who order.",
      },
    ],
  },
  {
    heading: "Not by email",
    options: [
      {
        value: INVOICE_SOURCE.vendorPortal,
        label: "A vendor website",
        detail: "You sign in to each supplier's site to get the invoice.",
      },
      {
        value: INVOICE_SOURCE.softwarePortal,
        label: "Another software tool",
        detail: "An accounting or ordering tool holds the invoices.",
      },
      {
        value: INVOICE_SOURCE.paper,
        label: "Paper",
        detail: "They come in the post, or a driver hands them over.",
      },
      {
        value: INVOICE_SOURCE.textMessage,
        label: "Text message",
        detail: "A supplier texts you a photo or a link.",
      },
      {
        value: INVOICE_SOURCE.fax,
        label: "Fax",
        detail: "A fax machine or a fax service receives them.",
      },
      {
        value: INVOICE_SOURCE.other,
        label: "Another way",
        detail: "Anything that is not listed here.",
      },
    ],
  },
];

export const InvoiceSourcesScreen = ({
  businessId,
  savedAnswers,
}: {
  businessId: string;
  savedAnswers: OnboardingAnswers;
}) => {
  const [sources, setSources] = useState<InvoiceSource[]>(
    savedAnswers.invoice_sources ?? [],
  );
  const [other, setOther] = useState(savedAnswers.invoice_sources_other ?? "");
  const [summary, setSummary] = useState(
    savedAnswers.invoice_sources_summary ?? "",
  );

  const toggle = (value: InvoiceSource, checked: boolean) =>
    setSources((current) =>
      checked
        ? [...current, value]
        : current.filter((source) => source !== value),
    );

  const otherChosen = sources.includes(INVOICE_SOURCE.other);

  return (
    <StepLayout
      activePane="invoice-sources"
      title="Where do your invoices come from?"
      subtitle="Choose every way you get them today. Your answer decides what we ask next."
      nav={
        <StepNavigation
          businessId={businessId}
          step="invoice_sources"
          answers={{
            invoice_sources: sources,
            invoice_sources_other: otherChosen ? other : "",
            invoice_sources_summary: summary,
          }}
          nextDisabled={!sources.length}
        />
      }
    >
      <Card className="w-full gap-0 py-0">
        <CardContent className="p-6">
          <Stack className="w-full" gap={6} align="stretch">
            {SOURCE_GROUPS.map(({ heading, options }) => (
              <Stack className="w-full" key={heading} gap={2} align="stretch">
                <Text weight="semibold" variant="caption" tone="muted">
                  {heading}
                </Text>
                {options.map(({ value, label, detail }) => (
                  <Stack
                    className="w-full"
                    key={value}
                    direction="row"
                    gap={2}
                    align="start"
                  >
                    <Checkbox
                      id={`invoice-source-${value}`}
                      className="mt-1"
                      checked={sources.includes(value)}
                      onCheckedChange={(checked) =>
                        toggle(value, checked === true)
                      }
                    />
                    <Stack className="w-full" align="start" gap={0}>
                      <Label htmlFor={`invoice-source-${value}`}>{label}</Label>
                      <Text variant="caption" tone="muted">
                        {detail}
                      </Text>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            ))}

            {otherChosen && (
              <Stack className="w-full" gap={1} align="stretch">
                <Label htmlFor="invoice-sources-other">
                  Tell us how they reach you
                </Label>
                <Input
                  id="invoice-sources-other"
                  value={other}
                  maxLength={255}
                  placeholder="For example: the supplier posts them to us"
                  onChange={(event) => setOther(event.target.value)}
                />
              </Stack>
            )}

            {/* The boxes say which routes exist. This is where the shape of the
                setup comes out: who handles them, what is messy, what they already
                know is a problem. It is optional, because a customer who has
                nothing to add should not be held on the step. */}
            <Stack
              gap={1}
              align="stretch"
              className="w-full border-t border-line pt-6"
            >
              <Label htmlFor="invoice-sources-summary">
                How does it work today?
              </Label>
              <Text variant="caption" tone="muted">
                A couple of sentences is plenty. Optional.
              </Text>
              <Textarea
                id="invoice-sources-summary"
                value={summary}
                maxLength={INVOICE_SOURCES_SUMMARY_LIMIT}
                className="text-md placeholder:text-md min-h-24"
                placeholder="For example: most land in ap@, a few vendors post them, and our techs get some on their phones."
                onChange={(event) => setSummary(event.target.value)}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </StepLayout>
  );
};
